import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankConsignmentTransportEquipment } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormField from 'components/p5/FormField'
import Input from 'components/p5/Input'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormSelect from 'components/p5/FormSelect'
import RadioGroup from 'components/p5/RadioGroup'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import GoodsReferences from './GoodsReferences'
import Seals from './Seals'

const scope = 'transportEquipment'

function ConsignmentTransportEquipment() {
  const { t } = useTranslation()

  const { control } = useFormContext<TransitOperationFormValues>()
  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, 'transportEquipment'>({
    control,
    name: scope,
  })

  const [, containerSizeAndIdentificationOptions] = useCodelist('CONTAINER_SIZE_AND_TYPE_IDENTIFICATIONS')
  const [, containerPackedStatusOptions] = useCodelist('CONTAINER_PACKED_STATUSES')
  const [, containerSupplierTypeOptions] = useCodelist('CONTAINER_SUPPLIER_TYPES')

  const paths = useMemo(() => [
    scope,
    'containerIndicator',
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.containerIdentificationNumber`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.containerSizeAndType`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.packedStatus`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.supplierType`),
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.goodsReferences`),
  ], [visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)

  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-section"
        headingLabel={t('declaration.p5.transportEquipment')}
      >
        <FormField
          labelKey="declaration.p5.containerisedIndicator"
          fieldRule={fieldRequirements.containerIndicator}
        >
          <RadioGroup
            field="containerIndicator"
            type="radio"
            radioOptions={[
              { label: 'common.no', value: 'false' },
              { label: 'common.yes', value: 'true' },
            ]}
            fieldRule={fieldRequirements.containerIndicator}
          />
        </FormField>
        {
          visibleFields.map((fieldWithIndex) => {
            const equipmentItem = fieldWithIndex.field
            const innerItemScope = `transportEquipment.${fieldWithIndex.index}` as const

            return (
              <FormFieldArray
                key={`equipmentItem_${fieldWithIndex.index}`}
                titleKey="declaration.p5.sequenceNumber"
                sequenceNumber={equipmentItem.sequenceNumber + 1}
                removeItem={() => softRemove(fieldWithIndex.index)}
                hidden={equipmentItem.deleted}
              >
                <FormField
                  labelKey="declaration.p5.containerNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.containerIdentificationNumber`]}
                >
                  <Input
                    field={`${innerItemScope}.containerIdentificationNumber`}
                    labelKey="declaration.p5.containerNumber"
                    type="text"
                    placeholder={t('common.fieldPlaceholder')}
                    fieldRule={fieldRequirements[`${innerItemScope}.containerIdentificationNumber`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.containerSizeAndType"
                  fieldRule={fieldRequirements[`${innerItemScope}.containerSizeAndType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.containerSizeAndType`}
                    labelKey="declaration.p5.containerSizeAndType"
                    options={containerSizeAndIdentificationOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.containerSizeAndType`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.containerPackedStatus"
                  fieldRule={fieldRequirements[`${innerItemScope}.packedStatus`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.packedStatus`}
                    labelKey="declaration.p5.containerPackedStatus"
                    options={containerPackedStatusOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.packedStatus`]}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.containerSupplierType"
                  fieldRule={fieldRequirements[`${innerItemScope}.supplierType`]}
                >
                  <FormSelect
                    field={`${innerItemScope}.supplierType`}
                    labelKey="declaration.p5.containerSupplierType"
                    options={containerSupplierTypeOptions}
                    type="sync"
                    fieldRule={fieldRequirements[`${innerItemScope}.supplierType`]}
                  />
                </FormField>
                <GoodsReferences transportEquipmentIndex={fieldWithIndex.index} />
                <Seals transportEquipmentIndex={fieldWithIndex.index} />
              </FormFieldArray>
            )
          })
        }
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankConsignmentTransportEquipment)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewContainer')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default ConsignmentTransportEquipment
